const MVCObject = require("../mvcobject").MVCObject;
const TemplateRenderer = require("../templaterenderer").TemplateRenderer;
const $ = require("../jquery_with_plugins");

/**
 * @typedef {Object} SelectedAndUnselectedRendererOptions
 * @property {google.maps.DirectionsRendererOptions} unselectedRenderersOptions options for unselected renderers.
 * @property {google.maps.DirectionsRendererOptions} selectedRendererOptions options for selected renderer.
 */

/**
 * You must bind directionsLink property to directionsProvider
 * if you want to handle the click button on an object
 * with the CSS class woosmap-mobile-link-button to redirect mobile user on Gmaps app.
 * @constructor
 * @param {google.maps.Map} map the google map which will display route polyline
 * @param {String} summaryTemplate html template for the route summary
 * @param {Function} displayNewRenderersCallback callback calling after all renderers are set
 * @param {SelectedAndUnselectedRendererOptions} selectedAndUnselectedRendererOptions
 * options for both selected an unselected renderers.
 * @extends woosmap.utils.MVCObject
 * @memberOf woosmap.ui
 */
class DirectionsResultsDisplayer extends MVCObject {
    constructor(map, summaryTemplate, displayNewRenderersCallback, selectedAndUnselectedRendererOptions) {
        super();
        this.$summaryContainer = $("<div>");
        this.$stepsContainer = $("<div>");
        this.summaryRenderer = new TemplateRenderer(summaryTemplate);
        this._map = map;
        this.displayNewRenderersCallback = displayNewRenderersCallback;
        this.directionsLink = null;
        if (selectedAndUnselectedRendererOptions) {
            this.unselectedRenderersOptions = selectedAndUnselectedRendererOptions.unselectedRenderersOptions;
            this.selectedRendererOptions = selectedAndUnselectedRendererOptions.selectedRendererOptions;
        }
    }

    directionsRenderers_changed() {
        this.$summaryContainer.empty();
        this.$stepsContainer.empty();
        const summaries = this.get("directionsSummaries");

        if (summaries) {
            summaries.forEach((routeInfo, index) => {
                const routeSummary = this.summaryRenderer.render(routeInfo);
                this.$summaryContainer.append(routeSummary);
            });
        }

        if ("desktop" === "mobile") {
            this.$summaryContainer
                .find(".woosmap-mobile-link-button")
                .show()
                .click(() => {
                    if (this.get("directionsLink")) {
                        window.location.href = this.get("directionsLink");
                    } else {
                        this.$summaryContainer.find(".woosmap-mobile-link-button").hide();
                    }
                })
                .show();
        } else {
            this.$summaryContainer.find(".woosmap-mobile-link-button").hide();
        }
        if (this.get("directionsRenderers").length > 0) {
            this.displayNewRenderersCallback();
        }
    }

    /**
     * Display route's steps on panel
     * @param {Number} rendererIndex the index of the renderer which contain direction to display
     */
    displayRouteSteps(rendererIndex) {
        this.get("directionsRenderers")[rendererIndex].setPanel(this.$stepsContainer[0]);
    }

    /**
     * Clean all routes on panel
     */
    cleanRoutes() {
        this.$summaryContainer.empty();
    }

    /**
     * Clean all route's step on panel
     */
    cleanRouteSteps() {
        this.$stepsContainer.empty();
        this.get("directionsRenderers").forEach((renderer, index) => {
            renderer.setPanel(null);
        });
    }

    /**
     * Hide all route's Polyline on map
     */
    cleanMapFromRoutes() {
        this.get("directionsRenderers").forEach((renderer, index) => {
            renderer.setMap(null);
        });
    }

    /**
     * Display all the route on the map
     */
    displayAllRoutes() {
        this.get("directionsRenderers").forEach((renderer, index) => {
            renderer.setMap(this._map);
        });
    }

    /**
     * Display a selected route polyline on map
     * @param {Number} rendererIndex the index of the renderer which contain the route to display
     */
    displayRouteOnMap(rendererIndex) {
        this.setSelectedRenderer(rendererIndex);
        const renderer = this.get("directionsRenderers")[rendererIndex];
        renderer.setMap(this._map);
    }

    /**
     * Set renderer options to `selectedRendererOptions` and set others renderers options to `unselectedRenderersOptions`
     * @param {Number} rendererIndex the index of the renderer to set options.
     */
    setSelectedRenderer(rendererIndex) {
        this.get("directionsRenderers").forEach((renderer, index) => {
            if (index === rendererIndex) {
                renderer.setOptions(this.selectedRendererOptions);
            } else {
                renderer.setOptions(this.unselectedRenderersOptions);
            }
            renderer.setDirections(renderer.getDirections());
        });
    }

    getRoutesContainer() {
        return this.$summaryContainer;
    }

    getStepsContainer() {
        return this.$stepsContainer;
    }
}

exports.DirectionsResultsDisplayer = DirectionsResultsDisplayer;
